import React, { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { inr } from "@/lib/utils_pharma";
import { Plus, ChevronDown } from "lucide-react";

/**
 * Typeable medicine picker with search + "add new" option.
 * Props:
 *   value: current medicine id
 *   valueLabel: current medicine name (used as display when value is set)
 *   medicines: array of medicine objects (id, name, mrp, purchase_price, gst_percent, stock, batch_no, hsn_code)
 *   onSelect: (medicineObj) => void
 *   onAddNew: (query) => void
 *   testId: base data-testid
 */
export default function MedicineTypeahead({ value, valueLabel, medicines, onSelect, onAddNew, testId = "med-picker" }) {
  const [q, setQ] = useState(valueLabel || "");
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);

  useEffect(() => {
    setQ(valueLabel || "");
  }, [valueLabel]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = q.trim()
    ? medicines.filter((m) => m.name.toLowerCase().includes(q.toLowerCase())).slice(0, 20)
    : medicines.slice(0, 20);

  const pick = (m) => {
    onSelect(m);
    setQ(m.name);
    setOpen(false);
  };

  return (
    <div className="relative" ref={wrap}>
      <div className="relative">
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Type or select…"
          data-testid={testId}
          className="pr-7"
        />
        <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
      </div>

      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-border rounded-md max-h-64 overflow-auto shadow-md">
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">No matches</div>
          )}
          {filtered.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => pick(m)}
              className={`w-full text-left px-3 py-2 hover:bg-muted transition-colors border-b border-border last:border-0 ${
                m.id === value ? "bg-primary/5" : ""
              }`}
              data-testid={`${testId}-opt-${m.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">{m.name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {m.manufacturer || "—"} · HSN {m.hsn_code || "—"} · stock {m.stock}
                  </div>
                </div>
                <div className="text-xs tabular-nums text-muted-foreground">{inr(m.mrp)}</div>
              </div>
            </button>
          ))}
          <button
            type="button"
            onClick={() => { onAddNew(q); setOpen(false); }}
            data-testid={`${testId}-add-new`}
            className="w-full text-left flex items-center gap-2 px-3 py-2 bg-primary/5 hover:bg-primary/10 text-primary transition-colors"
          >
            <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground grid place-items-center">
              <Plus size={12} />
            </span>
            <span className="text-xs">
              {q.trim() ? <>Add <span className="font-semibold">"{q}"</span> as new medicine</> : <>Add new medicine</>}
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
